import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  Users, 
  Settings, 
  BarChart3, 
  Target, 
  Menu,
  X,
  Building,
  User,
  Shield,
  Activity,
  FileText,
  Building2,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<any>;
  roles: string[];
}

const navigation: NavItem[] = [
  {
    path: '/admin',
    label: 'Dashboard',
    icon: BarChart3,
    roles: ['ADMIN', 'COACH']
  },
  {
    path: '/admin/users',
    label: 'User Management',
    icon: Users,
    roles: ['ADMIN']
  },
  {
    path: '/admin/analytics',
    label: 'Analytics',
    icon: Activity,
    roles: ['ADMIN', 'COACH']
  },
  {
    path: '/admin/analytics/players',
    label: 'Player Analytics',
    icon: User,
    roles: ['ADMIN', 'COACH']
  },
  {
    path: '/shot-visualization',
    label: '3D Shot Visualization',
    icon: Target,
    roles: ['ADMIN', 'COACH', 'PLAYER']
  },
  {
    path: '/admin/coach/live-session',
    label: 'Live Session Monitor',
    icon: Target,
    roles: ['COACH', 'ADMIN']
  },
  {
    path: '/admin/coach/conversational-analytics',
    label: 'Conversational Analytics',
    icon: MessageSquare,
    roles: ['COACH', 'ADMIN']
  },
  {
    path: '/admin/reports',
    label: 'Reports & Communication',
    icon: FileText,
    roles: ['ADMIN', 'COACH']
  },
  {
    path: '/admin/academy',
    label: 'Academy Management',
    icon: Building2,
    roles: ['ADMIN', 'COACH']
  },
  {
    path: '/admin/organization',
    label: 'Organization',
    icon: Building,
    roles: ['ADMIN']
  },
  {
    path: '/admin/settings',
    label: 'Settings',
    icon: Settings,
    roles: ['ADMIN']
  }
];

export function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, organization } = useAuth();
  const location = useLocation();

  if (!user || !['ADMIN', 'COACH'].includes(user.role)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-xl font-semibold">Access Denied</h1>
          <p className="mt-2 text-muted-foreground">
            You don't have permission to access this area.
          </p>
        </div>
      </div>
    );
  }

  const allowedNavigation = navigation.filter(item => 
    item.roles.includes(user.role)
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">SixSight</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Organization info */}
        <div className="p-4 border-b">
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={organization?.branding?.logo} />
              <AvatarFallback>
                {organization?.name?.[0]?.toUpperCase() || 'O'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-sm font-medium">{organization?.name}</p>
              <div className="flex items-center space-x-2">
                <Badge 
                  variant={organization?.plan === 'ENTERPRISE' ? 'default' : 'secondary'}
                  className="text-xs"
                >
                  {organization?.plan}
                </Badge>
                <Badge 
                  variant={organization?.status === 'ACTIVE' ? 'success' : 'destructive'}
                  className="text-xs"
                >
                  {organization?.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="mt-4 px-2">
          {allowedNavigation.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin' && location.pathname.startsWith(item.path));
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`
                  flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                  ${isActive 
                    ? 'bg-primary text-primary-foreground' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <item.icon className="mr-3 h-5 w-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User info */}
        <div className="absolute bottom-0 w-full p-4 border-t">
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user.avatar || undefined} />
              <AvatarFallback>
                {user.name?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <Badge variant="outline" className="text-xs">
                {user.role}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Mobile header */}
        <div className="lg:hidden flex items-center justify-between h-16 px-4 bg-white shadow-sm border-b">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex items-center space-x-2">
            <Target className="w-6 h-6 text-primary" />
            <span className="font-bold">SixSight Admin</span>
          </div>
          <div className="w-10" /> {/* Spacer */}
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}